import { observer } from 'mobx-react-lite';
import React from 'react';

import { BookingAddOnSelector } from '@/booking-add-ons';
import { useBookingCheckout } from '@/booking-checkout';
import { BookingCheckoutEvents } from '@/booking-checkout/events';
import { useBookingPaymentProviderDetails } from '@/booking-payment/hooks/use-booking-payment-provider-details.hook';
import { useActiveBrandConfig } from '@/brand';
import { useAppEvents } from '@/events';
import { useHotel } from '@/hotel';
import { StripeProviderWithElements } from '@/payments';

import { AdditionalInfoForm } from '../components/additional-info/AdditionalInfoForm.component';
import { useBooking } from '../hooks';
import { AdditionalInfoFormFields } from '../validators/additional-info-form.validator';

interface AdditionalInfoFormContainerProps {
  bookingId: string;
  hotelSlug: string;
  onAfterSubmit: (hasRequestedOneClickCheckout: boolean) => void;
}

export const AdditionalInfoFormContainer = observer(
  ({
    bookingId,
    hotelSlug,
    onAfterSubmit,
  }: AdditionalInfoFormContainerProps) => {
    const { isFlexyTimeEnabled, displayAccessibleRoomCheckbox } =
      useActiveBrandConfig();

    const { setAdditionalFields, config } = useBookingCheckout(bookingId);

    const booking = useBooking({ bookingId, hotelSlug });
    const hotel = useHotel(hotelSlug);
    const { data: paymentDetails } = useBookingPaymentProviderDetails({
      bookingId,
      hotelReferenceId: hotelSlug,
    });

    // Analytics
    const events = useAppEvents<BookingCheckoutEvents>();

    if (!booking || !hotel) return null;

    const handleSubmit = (
      values: AdditionalInfoFormFields,
      hasRequestedOneClickCheckout: boolean
    ) => {
      const {
        childBed,
        childCot,
        accessRequirements,
        checkInTime,
        checkOutTime,
        ...rest
      } = values;

      setAdditionalFields({
        childBed: childBed.length > 0,
        childCot: childCot.length > 0,
        accessRequirements: accessRequirements.length > 0,
        checkInTime: checkInTime || undefined,
        checkOutTime: checkOutTime || undefined,
        dog: false,
        ...rest,
      });

      events.emit('additionalInfoFormProgression', { hotel, booking });

      onAfterSubmit(hasRequestedOneClickCheckout);
    };

    const { childBed, childCot, accessRequirements, ...rest } =
      config.additionalInfoFields ?? {};
    const initialValues = {
      ...rest,
      childBed: childBed ? ['bed'] : [],
      childCot: childCot ? ['cot'] : [],
      accessRequirements: accessRequirements ? ['accessRequirements'] : [],
    };

    return (
      <StripeProviderWithElements bookingId={bookingId} hotelSlug={hotelSlug}>
        <AdditionalInfoForm
          hotelReferenceId={hotelSlug}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          isChildBedSelectable={Boolean(
            // TODO: inspect whether OWS properties still need this selector
            booking.isChildBedSelectable && hotel.dialect === 'ows'
          )}
          isFlexyTimeAvailable={Boolean(
            isFlexyTimeEnabled && booking.metaData?.flexyCheckInOutAvailable
          )}
          paymentTime={booking.paymentTime}
          countryCode={paymentDetails.paymentIntent.country}
          currencyCode={hotel.payments.currencyCode}
          addOns={
            hotel.addOnsEnabled ? (
              <BookingAddOnSelector hotel={hotel} booking={booking} />
            ) : null
          }
          discountDetail={
            booking.firstRoomStay.firstRatePlan.chargeBreakdown.discountDetail
          }
          displayAccessibleRoomCheckbox={
            displayAccessibleRoomCheckbox &&
            hotel.operational.hasAccessibleRooms
          }
        />
      </StripeProviderWithElements>
    );
  }
);
